<template>
  <div>
    <h2 class="text-center mb-1 fw-bold">Just one more step</h2>
    <p class="text-center text-muted">
      Add a phone number or email so we can keep you posted on your ride.
    </p>

    <div v-if="errorMessage" class="alert alert-danger py-2" role="alert">{{ errorMessage }}</div>
    <div v-if="infoMessage" class="alert alert-info py-2" role="alert">{{ infoMessage }}</div>

    <!-- Existing-account conflict: offer to link instead of a dead-end error -->
    <div v-if="hasExistingProfile" class="text-center">
      <p class="text-muted small">
        Linking will sign that account out on any other device it's currently signed into.
      </p>
      <button class="btn btn-primary btn-block w-100 mb-2" type="button"
              :disabled="isBusy" @click="linkProfile">
        <span v-if="isBusy" class="spinner-border spinner-border-sm me-2"></span>
        {{ isBusy ? 'Linking...' : 'Link this profile' }}
      </button>
      <button class="btn btn-link p-0" type="button" :disabled="isBusy" @click="resetToVerification()">
        Use a different email or phone number
      </button>
    </div>

    <template v-else>
    <!-- Channel toggle -->
    <div class="btn-group w-100 mb-3" role="group" aria-label="Verification channel">
      <button type="button" class="btn"
              :class="channel === 'phone' ? 'btn-primary' : 'btn-outline-primary'"
              :disabled="otpSent || isBusy" @click="setChannel('phone')">
        <i class="fa fa-phone me-1"></i> Phone
      </button>
      <button type="button" class="btn"
              :class="channel === 'email' ? 'btn-primary' : 'btn-outline-primary'"
              :disabled="otpSent || isBusy" @click="setChannel('email')">
        <i class="fa fa-envelope me-1"></i> Email
      </button>
    </div>

    <form class="theme-form" @submit.prevent="onSubmit">
      <div v-if="channel === 'email'" class="form-group">
        <label class="col-form-label">Email address</label>
        <input class="form-control" type="email" v-model.trim="email"
               :disabled="otpSent" placeholder="email@address.com"/>
      </div>
      <div v-else class="form-group">
        <label class="col-form-label">Phone number</label>
        <div class="input-group">
          <select class="form-select" v-model="countryCode" :disabled="otpSent" style="max-width: 120px;">
            <option value="+1">+1 (US)</option>
            <option value="+1">+1 (CA)</option>
          </select>
          <input class="form-control" type="tel" v-model.trim="phoneNumber"
                 :disabled="otpSent" placeholder="5551234567"/>
        </div>
      </div>

      <div class="form-group">
        <label class="col-form-label">Referral code <span class="text-muted">(optional)</span></label>
        <input class="form-control" type="text" v-model.trim="referralCode"
               :disabled="otpSent" placeholder="Have a friend's code?"/>
      </div>

      <div v-if="otpSent" class="form-group">
        <label class="col-form-label">Verification code</label>
        <input class="form-control" type="text" inputmode="numeric" v-model.trim="otpCode"
               placeholder="Enter the code we sent you"/>
        <div class="text-end mt-2">
          <button type="button" class="btn btn-link p-0"
                  :disabled="resendSeconds > 0 || isBusy" @click="sendCode">
            {{ resendSeconds > 0 ? `Resend in ${resendSeconds}s` : 'Resend code' }}
          </button>
        </div>
      </div>

      <div class="text-end mt-3">
        <button class="btn btn-primary btn-block w-100" type="submit" :disabled="isBusy">
          <span v-if="isBusy" class="spinner-border spinner-border-sm me-2"></span>
          {{ otpSent ? 'Finish' : 'Send code' }}
        </button>
      </div>
    </form>
    </template>
  </div>
</template>

<script setup>
import {ref, computed, onUnmounted} from 'vue';
import {RIDER_VERIFY_NUDGE_DISMISS_KEY} from '../utility/riderVerifyNudge.js';

const channel = ref('phone');     // 'phone' | 'email'
const email = ref('');
const countryCode = ref('+1');
const phoneNumber = ref('');
const referralCode = ref('');
const otpCode = ref('');
const otpSent = ref(false);
const loading = ref(false);
const resendSeconds = ref(0);
const errorMessage = ref(null);
const infoMessage = ref(null);
const hasExistingProfile = ref(false);

let resendTimer = null;

const isEmail = computed(() => channel.value === 'email');
const isBusy = computed(() => loading.value);

// The contact sent to the backend: raw email, or country code + phone digits.
const effectiveIdentifier = computed(() =>
    isEmail.value ? email.value : `${countryCode.value}${phoneNumber.value}`);

function setChannel(next) {
  if (otpSent.value) return; // don't switch mid-verification
  channel.value = next;
  errorMessage.value = null;
}

function startResendCountdown(seconds = 30) {
  stopResend();
  resendSeconds.value = seconds;
  resendTimer = setInterval(() => {
    if (resendSeconds.value > 0) resendSeconds.value -= 1;
    else stopResend();
  }, 1000);
}

function stopResend() {
  if (resendTimer) {
    clearInterval(resendTimer);
    resendTimer = null;
  }
}

async function postJson(url, payload) {
  const res = await fetch(url, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify(payload),
  });
  let data = null;
  try {
    data = await res.json();
  } catch (e) { /* may be a plain error string */
  }
  if (!res.ok) {
    const text = data ? (data.message || '') : await res.text().catch(() => '');
    throw new Error(text || 'Something went wrong. Please try again.');
  }
  return data || {};
}

async function sendCode() {
  errorMessage.value = null;
  infoMessage.value = null;

  if (isEmail.value ? !email.value : !phoneNumber.value) {
    errorMessage.value = isEmail.value ? 'Please enter your email.' : 'Please enter your phone number.';
    return;
  }

  loading.value = true;
  try {
    const data = await postJson('/api/portal/rider/send-signup-otp', {
      userIdentifier: effectiveIdentifier.value,
      isEmail: isEmail.value,
    });
    if (data.success === false) {
      errorMessage.value = data.message || 'Unable to send verification code.';
      return;
    }
    otpSent.value = true;
    infoMessage.value = data.message || 'Verification code sent.';
    startResendCountdown(30);
  } catch (e) {
    errorMessage.value = e.message;
  } finally {
    loading.value = false;
  }
}

async function onSubmit() {
  if (!otpSent.value) {
    await sendCode();
    return;
  }

  errorMessage.value = null;
  if (!otpCode.value) {
    errorMessage.value = 'Please enter the verification code.';
    return;
  }

  loading.value = true;
  try {
    const data = await postJson('/api/portal/rider/complete-profile', {
      email: isEmail.value ? email.value : null,
      phoneNumber: isEmail.value ? null : effectiveIdentifier.value,
      otpCode: otpCode.value,
      otpIsEmail: isEmail.value,
      referralCode: referralCode.value || null,
    });
    if (data.success && data.redirectUrl) {
      // A fresh login-like landing always re-arms the verify-contact nudge, even if it was
      // dismissed in an earlier session.
      sessionStorage.removeItem(RIDER_VERIFY_NUDGE_DISMISS_KEY);
      window.location.href = data.redirectUrl;
    } else if (data.hasExistingProfile) {
      hasExistingProfile.value = true;
      errorMessage.value = data.message || 'This contact is already linked to another account.';
    } else {
      errorMessage.value = data.message || 'Unable to complete your profile.';
    }
  } catch (e) {
    errorMessage.value = e.message;
  } finally {
    loading.value = false;
  }
}

// Resets back to the verification step. Used both when linking fails (the OTP-verified
// state it depends on may have expired) and when the rider wants to try a different
// contact instead of linking - re-verifying is a safe recovery path either way.
function resetToVerification(message = null) {
  errorMessage.value = message;
  hasExistingProfile.value = false;
  otpSent.value = false;
  otpCode.value = '';
}

async function linkProfile() {
  errorMessage.value = null;
  loading.value = true;
  try {
    const data = await postJson('/api/portal/rider/link-profile', {
      email: isEmail.value ? email.value : null,
      phoneNumber: isEmail.value ? null : effectiveIdentifier.value,
      otpIsEmail: isEmail.value,
    });
    if (data.success && data.redirectUrl) {
      // A fresh login-like landing always re-arms the verify-contact nudge, even if it was
      // dismissed in an earlier session.
      sessionStorage.removeItem(RIDER_VERIFY_NUDGE_DISMISS_KEY);
      window.location.href = data.redirectUrl;
    } else {
      resetToVerification(data.message || 'Unable to link this profile. Please verify again.');
    }
  } catch (e) {
    resetToVerification(e.message);
  } finally {
    loading.value = false;
  }
}

onUnmounted(stopResend);
</script>
